/**
 * Session 60.21 · Saved Spaces
 *
 * Reusable Save button + tiny in-memory cache of the current member's
 * saved space IDs. Every card that supports saving reads from + writes to
 * this cache so the heart state stays consistent across the app without
 * refetching per-card. Cache invalidates on logout and on any successful
 * save/unsave.
 */
import { useCallback, useEffect, useState } from "react";
import api from "./api";

let _cache = null;         // { cafe_ids: Set, workspace_ids: Set } | null
let _loading = null;       // in-flight promise
const _subscribers = new Set();

function _publish() {
  for (const fn of _subscribers) {
    try { fn(); } catch { /* ignore */ }
  }
}

export function invalidateSavedSpaces() {
  _cache = null;
  _publish();
}

async function _load() {
  if (_cache) return _cache;
  if (_loading) return _loading;
  _loading = (async () => {
    try {
      const r = await api.get("/me/saved-spaces/ids");
      _cache = {
        cafe_ids: new Set(r.data?.cafe_ids || []),
        workspace_ids: new Set(r.data?.workspace_ids || []),
      };
    } catch {
      _cache = { cafe_ids: new Set(), workspace_ids: new Set() };
    } finally {
      _loading = null;
    }
    return _cache;
  })();
  return _loading;
}

/**
 * useIsSaved — subscribes a component to a specific space's saved state.
 * Returns [isSaved, toggle, busy] tuple.
 */
export function useIsSaved(spaceType, spaceId) {
  const [saved, setSaved] = useState(false);
  const [busy, setBusy] = useState(false);
  const [, force] = useState(0);

  useEffect(() => {
    let cancelled = false;
    _load().then(c => {
      if (cancelled || !c) return;
      const set = spaceType === "cafe" ? c.cafe_ids : c.workspace_ids;
      setSaved(set.has(spaceId));
    });
    const fn = () => {
      const c = _cache;
      if (!c) return;
      const set = spaceType === "cafe" ? c.cafe_ids : c.workspace_ids;
      setSaved(set.has(spaceId));
      force(n => n + 1);
    };
    _subscribers.add(fn);
    return () => { cancelled = true; _subscribers.delete(fn); };
  }, [spaceType, spaceId]);

  const toggle = useCallback(async () => {
    if (!spaceId || busy) return;
    setBusy(true);
    const nextSaved = !saved;
    try {
      if (nextSaved) {
        await api.post("/me/saved-spaces", { space_type: spaceType, space_id: spaceId });
      } else {
        await api.delete(`/me/saved-spaces/${spaceType}/${encodeURIComponent(spaceId)}`);
      }
      // Update local cache directly so every mounted card refreshes.
      const c = await _load();
      const set = spaceType === "cafe" ? c.cafe_ids : c.workspace_ids;
      if (nextSaved) set.add(spaceId); else set.delete(spaceId);
      _publish();
    } catch {
      // Non-fatal — leave prior state alone.
    } finally {
      setBusy(false);
    }
  }, [spaceType, spaceId, saved, busy]);

  return [saved, toggle, busy];
}
